import type { Action } from "../engine.js";
import type { QuickResult } from "../room.js";
import { activeMute } from "../rules.js";
import { addDays, dayKey, weekdayOfKey } from "../time.js";
import type { GameState } from "../types.js";
import { parsePolishDate } from "./dates.js";

/** Accusative weekday names: "odezwę się w sobotę". */
const WEEKDAY_ACC = ["niedzielę", "poniedziałek", "wtorek", "środę", "czwartek", "piątek", "sobotę"];

export function normalizeUtterance(text: string): string {
  let t = text
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  // Fillers that don't change the meaning of a command.
  for (let i = 0; i < 3; i++)
    t = t
      .replace(
        /^(dobra|dobrze|ok|okej|okay|no|weź|wez|proszę|prosze|hej|ej|słuchaj|sluchaj|kompanie|giermku) /,
        "",
      )
      .replace(/ (proszę|prosze|serio|już|juz|ok|okej|dobra|bo|na razie|narazie)$/, "");
  return t.trim();
}

const SHORT =
  /^(zamknij się|zamknij sie|zamknij ryj|cicho|cicho bądź|cicho badz|bądź cicho|badz cicho|cisza|ucisz się|ucisz sie|przestań|przestan|przestań gadać|przestan gadac|przestań mówić|przestan mowic|stop|dość|dosc|wystarczy|milcz|zamilcz|morda|spokój|spokoj)$/;
const TODAY =
  /^(daj mi spokój|daj mi spokoj|daj mi dziś spokój|daj mi dzisiaj spokój|daj mi dzis spokoj|daj mi dzisiaj spokoj|nie teraz|nie dziś|nie dzis|nie dzisiaj|odpuść|odpusc|odpuść dziś|odpusc dzis|odpuść sobie|zostaw mnie|zostaw mnie w spokoju|zostaw mnie dziś w spokoju|zostaw mnie dzisiaj w spokoju|zostaw mnie dzis w spokoju|daj mi dziś święty spokój|daj mi święty spokój|nie mam dziś siły|nie mam dzis sily|nie chce mi się gadać|nie chce mi sie gadac)$/;
const DAYS =
  /^(wyłącz się|wylacz sie|nie gadaj do mnie|nie gadaj|nie odzywaj się|nie odzywaj sie|nie zagaduj|daj mi spokój|daj mi spokoj|zrób sobie przerwę|zrob sobie przerwe|odpocznij ode mnie|zamilknij) (na|do|przez|aż do|az do) (.+)$/;
const UNMUTE =
  /^(wróć|wroc|wracaj|możesz gadać|mozesz gadac|możesz mówić|mozesz mowic|możesz już gadać|możesz już mówić|mozesz juz gadac|mozesz juz mowic|odezwij się|odezwij sie|odcisz się|odcisz sie|koniec ciszy|koniec wyciszenia|gadaj|już możesz|juz mozesz|możesz się odzywać|mozesz sie odzywac)$/;

/**
 * Deterministic handling of refusals and mutes (SPEC 6.3). Runs before the LLM:
 * instant, free and 100% reliable for the plain phrasings; anything longer goes to the model.
 */
export function quickIntent(text: string, state: GameState, now: number): QuickResult | null {
  const t = normalizeUtterance(text);
  if (!t || t.length > 60) return null;
  const tz = state.settings.timezone;
  const today = dayKey(now, tz);
  const mute = (actions: Action[], reply: string): QuickResult => ({
    actions,
    text: reply,
    endConversation: true,
    dismiss: true,
  });

  if (SHORT.test(t)) return mute([{ type: "mute", kind: "short" }], "Jasne.");
  if (TODAY.test(t)) return mute([{ type: "mute", kind: "today" }], "Jasne, nie przeszkadzam do jutra.");
  const d = DAYS.exec(t);
  if (d) {
    const tail = `${d[2]} ${d[3]}`;
    let until = parsePolishDate(tail, today);
    if (!until && /kilka|parę|pare|trochę|troche|chwil/.test(tail)) until = addDays(today, 3);
    if (!until) return null;
    if (until <= addDays(today, 1))
      return mute([{ type: "mute", kind: "today" }], "Jasne, nie przeszkadzam do jutra.");
    return mute(
      [{ type: "mute", kind: "days", untilDay: until }],
      `Dobra, odezwę się w ${WEEKDAY_ACC[weekdayOfKey(until)]}. Trzymaj się.`,
    );
  }
  if (UNMUTE.test(t)) {
    const was = activeMute(state.mute, now);
    return {
      actions: [{ type: "unmute" }],
      text: was ? "Jestem z powrotem. Jak się masz?" : "Jestem tu cały czas. Co słychać?",
      endConversation: false,
      dismiss: false,
    };
  }
  return null;
}
